import { useCallback, useEffect, useMemo, useState } from 'react'
import { Calendar } from 'lucide-react'
import api from '@/services/api'
import { useAuthStore } from '@/store/authStore'
import { cn } from '@/lib/utils'
import {
  getBotSettings,
  updateBotSettings,
  type TenantCommercialPolicy,
  type VerticalPolicyView,
} from '@/lib/api/botSettings'
import CommercialPolicyCard, { type PolicyViewStatus } from '@/components/bot/CommercialPolicyCard'
import { getChannelBusinessProfile, type ChannelBusinessProfileResponse } from '@/services/channelsApi'
import { normalizeToVertical, getVerticalProfile } from '@/lib/verticalUI'

interface Step3bBusinessProfileProps {
  businessType?: string
  targetAudience?: string
  channelInstanceId?: string | null
  initialCommercialPolicy?: TenantCommercialPolicy | null
  onCommercialPolicyChange?: (policy: TenantCommercialPolicy) => void
  onBack: () => void
  onSkip: () => void
  onNext: (result?: { faqsCreated?: number; faqQuestions?: string[] }) => void
}

const PAYMENT_METHODS = [
  'Efectivo',
  'Transferencia',
  'Zinli',
  'PayPal',
  'Pago Móvil',
  'Tarjeta de Débito',
  'Tarjeta de Crédito',
  'Binance Pay',
  'USDT',
]

const RETURN_POLICIES = [
  { id: 'none', label: 'No acepto devoluciones' },
  { id: '7_days', label: '7 días con empaque original' },
  { id: '15_days', label: '15 días sin condiciones' },
  { id: 'custom', label: 'Personalizar' },
]

const DEFAULT_SHIPPING_INFO =
  'Sí, hacemos envíos a todo el país. El costo varía según la ubicación: Caracas $5, otras ciudades $10-15. Envíos gratis en compras mayores a $100.'

const VALUE_PROPOSITION_SUGGESTIONS: Record<string, string[]> = {
  retail: [
    'Encuentra todo lo que necesitas en un solo lugar, con entrega el mismo día o retiro en tienda en menos de 1 hora.',
    'Curaduría de productos únicos que no verás en otro lado, a precios que se ajustan a tu bolsillo.',
  ],
  tienda_repuestos: [
    'Repuestos originales y reemplazos certificados para todas las marcas, con despacho express a tu taller o retiro inmediato en mostrador.',
    'Asesoría técnica automotriz precisa: garantizamos la compatibilidad exacta de la pieza por año y modelo antes de despachar.',
  ],
  servicios: [
    'Optimizamos tus procesos para reducir costos operativos en un 20% en los primeros seis meses; si no lo logramos, no pagas.',
    'No somos solo consultores; somos tu equipo extendido disponible 24/7 para que nunca tomes una decisión solo.',
  ],
  restaurante: [
    'Más que una cena, un viaje a Italia a través de ingredientes auténticos y recetas familiares en el corazón de la ciudad.',
    'Comida real, preparada al momento y lista en 10 minutos para quienes no quieren sacrificar su salud por su agenda.',
  ],
  tecnologia: [
    'La plataforma de gestión que tu equipo realmente usará. Configuración en 5 minutos, sin necesidad de manuales.',
    'Libera 10 horas de tu semana automatizando tareas repetitivas con nuestra IA de alta precisión.',
  ],
  salud: [
    'No tratamos síntomas, cuidamos personas. Un enfoque 360° que combina nutrición, medicina y salud mental.',
    'Entrena a tu ritmo con programas personalizados que se adaptan a tu nivel y horario, garantizando resultados en 90 días.',
  ],
  educacion: [
    'Domina las habilidades que el mercado laboral exige hoy mediante proyectos reales, no solo teoría de libros.',
    'Clases en vivo con expertos de la industria que te guían paso a paso hasta que consigas tu certificación.',
  ],
  bodegon_licoreria: [
    'Licores, snacks y hielo al instante en Caracas. Tu pedido llega frío en 20-35 minutos con motorizado express.',
    'Combos fiesteros con el mejor precio y ahorro garantizado a tasa oficial BCV sin recargos.',
  ],
  belleza_estetica: [
    'Resalta tu belleza con estilistas y especialistas de primer nivel en Caracas (Chacao y Las Mercedes).',
    'Tratamientos capilares, manicure spa y estética con atención personalizada por previa cita.',
  ],
  ferreteria: [
    'Materiales de construcción, electricidad y plomería con despacho rápido y flete pesado en Caracas.',
    'Cotización inmediata ítem por ítem, flete en camión 350 y facturación formal con RIF a tasa BCV.',
  ],
  inmobiliaria: [
    'Asesoría inmobiliaria premium en Caracas (Altamira, Chacao, Las Mercedes, El Hatillo).',
    'Inmuebles residenciales y comerciales con pozo de agua, planta eléctrica y seguridad garantizada.',
  ],
  turismo_posadas: [
    'Hospedaje boutique y paseos en lancha deportiva a los mejores cayos de Venezuela (Morrocoy, Los Roques).',
    'Planta eléctrica 100%, pozo propio, WiFi Starlink de alta velocidad y gastronomía caribeña.',
  ],
  otro: [
    'Atención rápida y personalizada, con soluciones claras desde el primer contacto.',
    'Calidad y confianza en cada compra: te acompañamos antes, durante y después.',
  ],
}

const LOCAL_SLANG_SUGGESTIONS_BY_BUSINESS_TYPE: Record<string, string[]> = {
  retail: [
    'Clientes (tienda): "apartado" (reservar), "delivery" (envío), "retiro" (pick-up), "pago móvil", "transferencia", "contra entrega".',
    'Clientes (tienda): "oferta"/"promo", "combito" (combo), "talla"/"medida", "disponible", "agotado".',
  ],
  tienda_repuestos: [
    'Clientes (repuestos): "original / reemplazo", "filtro", "pastillas de freno", "amortiguador", "bomba de gasolina", "kit de tiempo".',
    'Clientes (repuestos): "¿le sirve a mi carro?", "año / modelo", "retiro en mostrador", "envío al taller", "pago móvil", "transferencia".',
  ],
  servicios: [
    'Clientes (servicios): "presupuesto"/"cotización", "agenda"/"cita", "asesoría", "plan", "paquete", "mensualidad".',
    'Clientes (servicios): "¿lo cuadramos?" (coordinar), "¿me lo puedes pasar por WhatsApp?", "¿te sirve tal hora?".',
  ],
  restaurante: [
    'Clientes (comida): "para llevar", "delivery", "combo", "salsa aparte", "sin picante", "con extra".',
    'Clientes (comida): "¿cuánto tarda?", "¿hacen envíos?", "¿hasta qué hora?", "pago móvil".',
  ],
  tecnologia: [
    'Clientes (tech): "equipo" (celular/laptop), "pila" (batería), "memoria", "almacenamiento", "cargador", "protector", "forro".',
    'Clientes (tech): "liberado", "dual SIM", "cámara", "garantía", "repuesto", "servicio técnico".',
  ],
  salud: [
    'Clientes (salud): "consulta", "cita", "control", "evaluación", "examen", "resultados".',
    'Clientes (salud): "¿me puedes agendar?", "¿atienden por WhatsApp?", "¿cuánto cuesta?", "seguro" (si aplica).',
  ],
  educacion: [
    'Clientes (educación): "horarios", "inscripción", "mensualidad", "certificado", "clases en vivo", "grabadas".',
    'Clientes (educación): "pensum", "nivel", "cupos", "¿cuándo empieza?", "material".',
  ],
  bodegon_licoreria: [
    'Clientes: "curda", "frías", "promo", "combo fiestero", "ron Cacique / Santa Teresa", "cerveza Polar / Solera", "pinta", "hielo", "¿tienen vuelto de $20 / $50?".',
    'Modalidades: "delivery express frío", "pickup". Pagos: "Pago Móvil tasa BCV", "efectivo con vuelto".',
  ],
  belleza_estetica: [
    'Clientes: "balayage", "keratina brasileña", "manicure spa", "corte y secado", "abono de $5 para congelar turno", "cita presencial".',
    'Modalidades: "atención por turnos", "evaluación según largo y volumen", "Pago Móvil / Transferencia".',
  ],
  ferreteria: [
    'Clientes: "calibre 10/12", "breaker 2x40", "cuñete de pintura", "saco de cemento", "tirro negro / teflón", "¿emiten factura con IVA / retención?", "¿tienen flete en camión 350 o retiro en patio?".',
    'Logística: "moto express para ligeros", "flete pesado camión 350 a pie de obra", "retiro en patio de carga".',
  ],
  inmobiliaria: [
    'Clientes: "apto amoblado", "pozo de agua", "planta eléctrica 100%", "esquema 3+3+1", "persona jurídica con balance", "canon mensual", "visita presencial".',
    'Negociación: "3 meses de depósito, 3 meses adelantados, 1 mes de honorarios profesionales".',
  ],
  turismo_posadas: [
    'Clientes: "paseo a los cayos (Cayo Sombrero, Playuela, Los Juanes)", "cava de hielo e hidratación", "planta eléctrica y pozo", "abono 50% para congelar cupo", "check-in 3:00 PM / check-out 12:00 PM".',
    'Planes: "solo hospedaje con desayuno criollo", "plan VIP todo incluido con lancha deportiva".',
  ],
  otro: [
    'General (clientes): "delivery" (envío), "apartado" (reservar), "pago móvil", "transferencia", "promo".',
    'General (clientes): "¿me puedes pasar info?", "¿cuánto tarda?", "¿tienen disponible?", "¿tienes stock?".',
  ],
}

export default function Step3b_BusinessProfile(props: Step3bBusinessProfileProps) {
  const tenantId = useAuthStore((s) => s.tenantId)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [channelProfile, setChannelProfile] = useState<ChannelBusinessProfileResponse | null>(null)
  const [profileLoading, setProfileLoading] = useState(false)

  const normalizedVertical = normalizeToVertical(props.businessType)
  const profile = getVerticalProfile(normalizedVertical)

  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<string[]>([])
  const [customPaymentMethod, setCustomPaymentMethod] = useState('')

  const [shippingInfo, setShippingInfo] = useState('')
  const [shippingZones, setShippingZones] = useState<string[]>([])
  const [newZone, setNewZone] = useState('')

  const [returnPolicyChoice, setReturnPolicyChoice] = useState<(typeof RETURN_POLICIES)[number]['id']>('none')
  const [customReturnPolicy, setCustomReturnPolicy] = useState('')

  const [warranty, setWarranty] = useState('')
  const [valueProposition, setValueProposition] = useState('')
  const [localSlang, setLocalSlang] = useState('')
  // Sin presets locales: los defaults del vertical los aplica el backend en el runtime.
  const [commercialPolicy, setCommercialPolicy] = useState<TenantCommercialPolicy | null>(
    () => props.initialCommercialPolicy ?? null
  )
  const [policyView, setPolicyView] = useState<VerticalPolicyView | null>(null)
  const [policyStatus, setPolicyStatus] = useState<PolicyViewStatus>('loading')
  // Si el backend ya informó el vertical, su `requiresAppointment` manda sobre el registro local del frontend.
  const hasFulfillment = policyView ? !policyView.runtime.requiresAppointment : profile.operational.hasFulfillment
  // Tiempos ya guardados: se reenvían tal cual para no pisarlos con textos fijos del cliente.
  const [savedDeliveryTimes, setSavedDeliveryTimes] = useState<{
    local: string | null
    general: string | null
    sla: string | null
  }>({ local: null, general: null, sla: null })

  const reloadPolicyView = useCallback(async () => {
    setPolicyStatus('loading')
    try {
      const data = await getBotSettings()
      setPolicyView(data.commercialPolicyView ?? null)
      setPolicyStatus(data.commercialPolicyView ? 'ready' : 'error')
    } catch {
      setPolicyStatus('error')
    }
  }, [])

  useEffect(() => {
    if (!tenantId) return

    let cancelled = false
    const loadSavedData = async () => {
      try {
        const [profileRes, botRes] = await Promise.allSettled([
          api.get(`/api/tenants/${tenantId}/profile`),
          api.get('/bot/settings'),
        ])

        if (cancelled) return

        if (profileRes.status === 'fulfilled' && profileRes.value.data) {
          const p = profileRes.value.data
          if (p.valueProposition) setValueProposition(p.valueProposition)
          if (p.localSlang) setLocalSlang(p.localSlang)
        }

        if (botRes.status === 'fulfilled' && botRes.value.data) {
          const bData = botRes.value.data as any
          setPolicyView(bData.commercialPolicyView ?? null)
          setPolicyStatus(bData.commercialPolicyView ? 'ready' : 'error')
          if (bData.commercialPolicyConfig) {
            setCommercialPolicy((prev) => prev ?? bData.commercialPolicyConfig)
          }
          const cfg = bData.checkoutConfig
          if (cfg) {
            setSavedDeliveryTimes({
              local: cfg.localDeliveryTime ?? null,
              general: cfg.deliveryTime ?? null,
              sla: cfg.slaValidationTime ?? null,
            })
            if (cfg.shippingInfo) setShippingInfo(cfg.shippingInfo)
            if (Array.isArray(cfg.shippingZones) && cfg.shippingZones.length > 0) {
              setShippingZones(cfg.shippingZones)
            }
            if (Array.isArray(cfg.paymentMethods) && cfg.paymentMethods.length > 0) {
              setSelectedPaymentMethods(cfg.paymentMethods)
            }
            if (cfg.warranty) setWarranty(cfg.warranty)
            if (cfg.returnPolicy) {
              const matched = RETURN_POLICIES.find((r) => r.label === cfg.returnPolicy || r.id === cfg.returnPolicy)
              if (matched) {
                setReturnPolicyChoice(matched.id)
              } else if (cfg.returnPolicy.includes('No aceptamos')) {
                setReturnPolicyChoice('none')
              } else if (cfg.returnPolicy.includes('7 días')) {
                setReturnPolicyChoice('7_days')
              } else if (cfg.returnPolicy.includes('15 días')) {
                setReturnPolicyChoice('15_days')
              } else {
                setReturnPolicyChoice('custom')
                setCustomReturnPolicy(cfg.returnPolicy)
              }
            }
          }
        }
      } catch (err) {
        console.warn('Error cargando perfil de negocio desde BD:', err)
        setPolicyStatus('error')
      }
    }

    loadSavedData()
    return () => {
      cancelled = true
    }
  }, [tenantId])

  useEffect(() => {
    if (hasFulfillment) {
      setShippingInfo((prev) => (prev.trim().length ? prev : DEFAULT_SHIPPING_INFO))
    }

    const suggestions = VALUE_PROPOSITION_SUGGESTIONS[props.businessType || '']
    if (suggestions?.length) {
      setValueProposition((prev) => (prev.trim().length ? prev : suggestions[0] || ''))
    }
  }, [props.businessType, hasFulfillment])

  useEffect(() => {
    if (!props.channelInstanceId) {
      setChannelProfile(null)
      return
    }

    let cancelled = false
    setProfileLoading(true)
    getChannelBusinessProfile(props.channelInstanceId)
      .then((profile) => {
        if (!cancelled) {
          setChannelProfile(profile)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setChannelProfile(null)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setProfileLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [props.channelInstanceId])

  const localSlangSuggestions = useMemo(() => {
    return (
      LOCAL_SLANG_SUGGESTIONS_BY_BUSINESS_TYPE[props.businessType || ''] ||
      LOCAL_SLANG_SUGGESTIONS_BY_BUSINESS_TYPE['otro']
    )
  }, [props.businessType])

  useEffect(() => {
    if (localSlangSuggestions.length === 0) return
    setLocalSlang((prev) => (prev.trim().length ? prev : localSlangSuggestions[0] || ''))
  }, [localSlangSuggestions])

  const valuePropSuggestions = useMemo(() => {
    return VALUE_PROPOSITION_SUGGESTIONS[props.businessType || ''] || VALUE_PROPOSITION_SUGGESTIONS['otro']
  }, [props.businessType])

  const computedPaymentMethods = useMemo(() => {
    const base = [...selectedPaymentMethods]
    const custom = customPaymentMethod.trim()
    if (custom) base.push(custom)
    return Array.from(new Set(base))
  }, [selectedPaymentMethods, customPaymentMethod])

  const computedReturnPolicy = useMemo(() => {
    if (returnPolicyChoice === 'custom') return customReturnPolicy.trim()
    if (returnPolicyChoice === '7_days') return 'Aceptamos devoluciones durante 7 días con empaque original.'
    if (returnPolicyChoice === '15_days') return 'Aceptamos devoluciones durante 15 días sin condiciones.'
    return 'No aceptamos devoluciones.'
  }, [returnPolicyChoice, customReturnPolicy])

  const shouldSeedReturnPolicy = useMemo(() => {
    if (returnPolicyChoice === 'custom') return customReturnPolicy.trim().length > 0
    return returnPolicyChoice !== 'none'
  }, [returnPolicyChoice, customReturnPolicy])

  const canSubmit = useMemo(() => {
    if (loading) return false
    const hasAny =
      computedPaymentMethods.length > 0 ||
      shippingInfo.trim().length > 0 ||
      shippingZones.length > 0 ||
      (returnPolicyChoice !== 'none' || customReturnPolicy.trim().length > 0) ||
      warranty.trim().length > 0 ||
      valueProposition.trim().length > 0 ||
      localSlang.trim().length > 0
    return hasAny
  }, [
    loading,
    computedPaymentMethods,
    shippingInfo,
    shippingZones,
    returnPolicyChoice,
    customReturnPolicy,
    warranty,
    valueProposition,
    localSlang,
  ])

  const togglePaymentMethod = (m: string) => {
    setSelectedPaymentMethods((prev) =>
      prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]
    )
  }

  const addZone = () => {
    const z = newZone.trim()
    if (!z) return
    setShippingZones((prev) => Array.from(new Set([...prev, z])))
    setNewZone('')
  }

  const removeZone = (z: string) => {
    setShippingZones((prev) => prev.filter((x) => x !== z))
  }

  const handleSkip = () => {
    props.onSkip()
  }

  const handleContinue = async () => {
    setError(null)

    if (!tenantId) {
      setError('No se detectó tenant activo.')
      return
    }

    try {
      setLoading(true)

      await updateBotSettings({
        checkoutConfig: {
          enabled: true,
          askMethod: true,
          askAddress: true,
          methodPrompt: '¿Vas a retirar en tienda o prefieres delivery/envio?',
          addressPrompt: 'Si es delivery/envio, comparteme tu direccion y una referencia para coordinarlo.',
          pickupInstructions:
            'Retiro en tienda disponible. Te confirmaremos disponibilidad y horario antes de que pases.',
          deliveryInstructions:
            'Si eliges delivery/envio, te pediremos direccion, referencia y cualquier indicacion importante.',
          shippingInfo: shippingInfo.trim() || null,
          shippingZones: [...new Set(shippingZones.map((zone) => zone.trim()).filter(Boolean))],
          paymentMethods: computedPaymentMethods,
          returnPolicy: shouldSeedReturnPolicy ? computedReturnPolicy.trim() : null,
          warranty: warranty.trim() || null,
          localDeliveryTime: savedDeliveryTimes.local,
          deliveryTime: savedDeliveryTimes.general,
          slaValidationTime: savedDeliveryTimes.sla,
        } as any,
        // null = el tenant no editó nada: el backend no pisa la política guardada ni inventa una.
        ...(commercialPolicy ? { commercialPolicyConfig: commercialPolicy } : {}),
      })

      await api.post(`/api/tenants/${tenantId}/profile`, {
        valueProposition: valueProposition.trim() || undefined,
        targetAudience: props.targetAudience?.trim() || undefined,
        localSlang: localSlang.trim() || undefined,
      })

      const seedPayload = {
        paymentMethods: computedPaymentMethods.length ? computedPaymentMethods : undefined,
        shippingInfo: shippingInfo.trim() || undefined,
        shippingZones: shippingZones.length ? shippingZones : undefined,
        returnPolicy: shouldSeedReturnPolicy ? computedReturnPolicy.trim() || undefined : undefined,
        warranty: warranty.trim() || undefined,
      }

      const { data } = await api.post(`/api/tenants/${tenantId}/faqs/seed`, seedPayload)

      props.onNext({ faqsCreated: data?.created, faqQuestions: data?.questions })
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.response?.data?.message || 'No se pudo guardar el perfil del negocio.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-4">
          <span className="text-2xl">💳</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Perfil del negocio</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Esto es opcional. Si completas este paso, crearemos FAQs operacionales automáticamente.
        </p>
      </div>

      {props.channelInstanceId && (
        <div className="max-w-4xl mx-auto">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold">
                WA
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-900">Cuenta conectada</p>
                <p className="text-xs text-slate-600">
                  {profileLoading
                    ? 'Verificando si la cuenta es personal o business...'
                    : channelProfile?.accountType === 'business'
                      ? `Cuenta business${channelProfile.verifiedName ? ` · ${channelProfile.verifiedName}` : ''}`
                      : channelProfile?.accountType === 'personal'
                        ? 'La cuenta parece ser personal. Para catálogo y automatizaciones, recomendamos una cuenta business.'
                        : 'No pudimos confirmar el tipo de cuenta todavía.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto space-y-6">
        <div className="bg-white rounded-xl border p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">¿Qué métodos de pago aceptas?</h3>
          <div className="flex flex-wrap gap-2">
            {PAYMENT_METHODS.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => togglePaymentMethod(m)}
                className={cn(
                  'px-3 py-2 rounded-lg border text-sm transition-all',
                  selectedPaymentMethods.includes(m)
                    ? 'border-[#075E54] bg-[#075E54]/5 text-[#075E54]'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                {selectedPaymentMethods.includes(m) ? '✅ ' : ''}
                {m}
              </button>
            ))}
          </div>

          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Otro (opcional)</label>
            <input
              value={customPaymentMethod}
              onChange={(e) => setCustomPaymentMethod(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-[#075E54] focus:border-transparent text-base"
              placeholder="Ej: CashApp"
            />
          </div>
        </div>

        {!hasFulfillment ? (
          <div className="bg-white rounded-xl border p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#075E54]" />
              Modalidad de atención y citas ({profile.label})
            </h3>
            <p className="text-sm text-gray-600">
              Este vertical opera mediante citas y atención presencial o remota. Tu asistente orientará a los clientes y registrará solicitudes en estado pendiente para confirmación de tu personal.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">🚚 ¿Cómo son tus envíos?</h3>
            <textarea
              value={shippingInfo}
              onChange={(e) => setShippingInfo(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-[#075E54] focus:border-transparent text-base resize-none"
              placeholder="Ej: Envíos a todo Venezuela, 2-5 días, $3-8"
            />

            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Zonas (opcional)</label>
              <div className="flex gap-2">
                <input
                  value={newZone}
                  onChange={(e) => setNewZone(e.target.value)}
                  className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-[#075E54] focus:border-transparent text-base"
                  placeholder="Ej: Caracas"
                />
                <button
                  type="button"
                  onClick={addZone}
                  className="px-4 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-medium"
                >
                  Agregar
                </button>
              </div>

              {shippingZones.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {shippingZones.map((z) => (
                    <button
                      key={z}
                      type="button"
                      onClick={() => removeZone(z)}
                      className="text-xs px-2 py-1 rounded-full border border-gray-200 bg-gray-50"
                      title="Eliminar"
                    >
                      {z} ×
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            {hasFulfillment ? '🔄 Política de devoluciones' : '🔄 Política de cancelaciones y reprogramación'}
          </h3>
          <div className="space-y-2">
            {RETURN_POLICIES.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="returnPolicy"
                  checked={returnPolicyChoice === p.id}
                  onChange={() => setReturnPolicyChoice(p.id)}
                />
                {p.label}
              </label>
            ))}
          </div>

          {returnPolicyChoice === 'custom' && (
            <div className="mt-3">
              <input
                value={customReturnPolicy}
                onChange={(e) => setCustomReturnPolicy(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-[#075E54] focus:border-transparent text-base"
                placeholder="Escribe tu política..."
              />
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">🛡️ Garantías (opcional)</h3>
          <p id="onboarding-warranty-help" className="text-sm text-gray-500 mb-3">
            La garantía se define producto por producto en tu catálogo: de la tienda, del fabricante o sin garantía
            (por ejemplo, los consumibles). Aquí puedes explicar cómo se tramita un reclamo.
          </p>
          <label htmlFor="onboarding-warranty" className="sr-only">
            Cómo se tramita un reclamo de garantía
          </label>
          <input
            id="onboarding-warranty"
            aria-describedby="onboarding-warranty-help"
            value={warranty}
            onChange={(e) => setWarranty(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-[#075E54] focus:border-transparent text-base"
            placeholder="Ej: Escríbenos con tu factura y revisamos tu caso con el proveedor"
          />
        </div>

        {/* Políticas comerciales especializadas según vertical */}
        <CommercialPolicyCard
          view={policyView}
          status={policyStatus}
          onRetry={() => void reloadPolicyView()}
          policy={commercialPolicy}
          onChange={(updated) => {
            setCommercialPolicy(updated)
            props.onCommercialPolicyChange?.(updated)
          }}
        />

        <div className="bg-white rounded-xl border p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">✨ ¿Tu propuesta de valor principal?</h3>
          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Sugerencias (opcional)</label>
            <select
              value=""
              onChange={(e) => {
                const v = e.target.value
                if (v) setValueProposition(v)
              }}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-[#075E54] focus:border-transparent text-base"
            >
              <option value="">Selecciona una sugerencia o escribe la tuya</option>
              {valuePropSuggestions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <input
            value={valueProposition}
            onChange={(e) => setValueProposition(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-[#075E54] focus:border-transparent text-base"
            placeholder={hasFulfillment ? "Ej: Precios bajos + envío rápido" : "Ej: Atención especializada + respuesta rápida"}
          />
        </div>

        <div className="bg-white rounded-xl border p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">💬 Jerga local de tus clientes (opcional)</h3>
          {localSlangSuggestions.length > 0 && (
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Sugerencias (opcional)</label>
              <select
                value=""
                onChange={(e) => {
                  const v = e.target.value
                  if (v) setLocalSlang(v)
                }}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-[#075E54] focus:border-transparent text-base"
              >
                <option value="">Selecciona una sugerencia o escribe la tuya</option>
                {localSlangSuggestions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          )}
          <input
            value={localSlang}
            onChange={(e) => setLocalSlang(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-[#075E54] focus:border-transparent text-base"
            placeholder='Ej: mis clientes dicen "equipo" para celular'
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="flex justify-between pt-6 border-t">
          <button
            type="button"
            onClick={props.onBack}
            className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            disabled={loading}
          >
            ← Atrás
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSkip}
              className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              disabled={loading}
            >
              Completar después
            </button>
            <button
              type="button"
              onClick={handleContinue}
              disabled={!canSubmit}
              className={cn(
                'px-6 py-2.5 rounded-lg font-medium transition-all',
                canSubmit
                  ? 'bg-[#075E54] hover:bg-[#064E46] text-white shadow-lg hover:shadow-xl'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              )}
            >
              {loading ? 'Guardando...' : 'Continuar →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
